import 'src/polyfills';
import React, { useEffect } from 'react';

import { routes } from '@aline/routes';
import { SnackbarProvider } from 'notistack';
import { Route, Routes as RouterRoutes } from 'react-router-dom';
import type { DataAttributesCompany, IntercomProps } from 'react-use-intercom';
import { useIntercom } from 'react-use-intercom';
import { BaseSnackbar } from 'src/components';
import { TierGuardModalProvider, UnauthenticatedModalProvider } from 'src/components/auth';
import { BackgroundTaskTray } from 'src/components/background-tasks';
import { INTERCOM_LAUNCHER_DEFAULTS } from 'src/components/intercom';
import { BulkExportTaskPanels } from 'src/components/repository/BulkExportTaskPanels';
import { DocumentStatusJobsPanel } from 'src/components/repository/DocumentStatusJobsPanel';
import { RequestDocAccess } from 'src/editor/RequestDocAccess';
import { defaultSnackBarDisplayTimeMS } from 'src/helpers';
import { useAppVersionChecker, useNotifier, useSubscribeToDocumentsUpdate } from 'src/hooks';
import { useSubscribeToDocumentStatusJobs } from 'src/hooks/document-status-jobs';
import { useSubscribeToWorkflowsUpdated } from 'src/hooks/workflow';
import { AppLayout } from 'src/layouts';
import { useSetSentryUser } from 'src/sentry';

import { useStreamingAgentEvents } from './components/ai/chat/chat-hooks/useStreamingAgentEvents';
import { SessionTimeOutModalProvider } from './components/auth/SessionTimeOutModal';
import { PreOnboardingContainer } from './components/onboarding/OnboardingContainer';
import { createRelativeRouter } from './helpers/router-helpers';
import { GrantAccessPage } from './pages/GrantAccessPage';
import { WorkflowDoneSigning } from './workflow/signing-outcome/WorkflowDoneSigning';
import { WorkflowReassigned } from './workflow/signing-outcome/WorkflowReassigned';

export function Routes(): JSX.Element | null {
  /**
   * Note: SnackbarProvider must be nested below the router so Snackbars can use links.
   * Intercom is provided at the app root so global error fallbacks can open support chat.
   */
  return (
    <SnackbarProvider
      Components={{
        default: BaseSnackbar,
        error: BaseSnackbar,
        info: BaseSnackbar,
        success: BaseSnackbar,
        warning: BaseSnackbar,
      }}
      autoHideDuration={defaultSnackBarDisplayTimeMS}
    >
      <AppBody />
    </SnackbarProvider>
  );
}

const { toRelativePath: toRelativeHomePath } = createRelativeRouter(routes.Home, {
  nestedRoutes: true,
});

function AppBody() {
  const { update } = useIntercom();
  const { currentUser } = useSetSentryUser();

  useNotifier();
  useAppVersionChecker();
  /**
   * The four subscriptions below read nothing back from the subscription result and set
   * `ignoreResults`, so an event never re-renders AppBody, and every open tab with it.
   */
  // Agent events streamed from here so they work in AI Chat in Dashboard and Editor.
  useStreamingAgentEvents();
  // Subscribe to documents updates so that OCR/other updates are reflected in AI Chat in Dashboard and in AI Reports.
  useSubscribeToDocumentsUpdate();
  // Patches the grid and feeds the progress panel for background status changes.
  useSubscribeToDocumentStatusJobs();
  // Merges workflow AI-build lifecycle updates into the Apollo cache as they happen.
  useSubscribeToWorkflowsUpdated();

  useEffect(() => {
    const company: DataAttributesCompany | undefined = currentUser?.org
      ? {
          companyId: currentUser.org.id,
          name: currentUser.org.name,
        }
      : undefined;
    const userProps: Partial<IntercomProps> = currentUser
      ? {
          createdAt: currentUser.createdAt,
          email: currentUser.email,
          name: currentUser.displayName,
          userId: currentUser.id,
        }
      : {};

    update({
      company,
      hideDefaultLauncher: true,
      ...INTERCOM_LAUNCHER_DEFAULTS,
      ...userProps,
    });
  }, [update, currentUser]);

  return (
    <TierGuardModalProvider>
      <SessionTimeOutModalProvider>
        <UnauthenticatedModalProvider>
          <BackgroundTaskTray>
            <RouterRoutes>
              {/* Bare routes — no tab bar, no AppLayout */}
              <Route element={<GrantAccessPage />} path={toRelativeHomePath(routes.GrantAccess)} />
              <Route
                element={<RequestDocAccess />}
                path={toRelativeHomePath(routes.RequestDocAccess)}
              />
              <Route
                element={<WorkflowDoneSigning />}
                path={toRelativeHomePath(routes.WorkflowDoneSigning)}
              />
              <Route
                element={<WorkflowReassigned />}
                path={toRelativeHomePath(routes.WorkflowReassigned)}
              />

              <Route element={<PreOnboardingContainer />} path={toRelativeHomePath(routes.Setup)} />

              {/* AppLayout renders tab content via TabActivityArea (not Outlet) */}
              <Route element={<AppLayout />} path="*" />
            </RouterRoutes>
            {Boolean(currentUser) && <DocumentStatusJobsPanel />}
            {Boolean(currentUser) && <BulkExportTaskPanels />}
          </BackgroundTaskTray>
        </UnauthenticatedModalProvider>
      </SessionTimeOutModalProvider>
    </TierGuardModalProvider>
  );
}
